"use client";

import { Button } from "@/components/ui/button";
import Pagination from "../../../Common/Pagination";
import { AlertTriangle, Edit, Eye, Wrench } from "lucide-react";

export default function AssetsTable({
  assets,
  searchTerm,
  handleAssetSort,
  getSortIcon,
  getStatusBadgeClass,
  getQuantitySnapshot,
  openEditForm,
  openViewDialog,
  setSelectedAsset,
  setShowMaintenanceForm,
  currentPage,
  totalAssets,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
}) {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-y border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleAssetSort("asset_name")}>
                <div className="flex items-center">Name {getSortIcon("asset_name")}</div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleAssetSort("category")}>
                <div className="flex items-center">Category {getSortIcon("category")}</div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Vendor</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Assigned To</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleAssetSort("condition")}>
                <div className="flex items-center">Condition {getSortIcon("condition")}</div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleAssetSort("status")}>
                <div className="flex items-center">Status {getSortIcon("status")}</div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Warranty</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Value</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Units</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {assets.length === 0 ? (
              <tr>
                <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                  {searchTerm ? "No assets found matching your search" : "No assets available"}
                </td>
              </tr>
            ) : (
              assets.map((asset) => {
                const { total: qTotal, inSvc, repair: qRepair, spare } = getQuantitySnapshot(asset);
                const warrantyExpiry = new Date(asset.warranty_expiry_date);
                const today = new Date();
                const daysUntilExpiry = Math.ceil((warrantyExpiry - today) / (1000 * 60 * 60 * 24));
                const isExpiringSoon = daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                const isExpired = daysUntilExpiry <= 0;
                const nextMaintenance = asset.maintenance_schedule?.next_date ? new Date(asset.maintenance_schedule.next_date) : null;
                const maintenanceDue = nextMaintenance && nextMaintenance <= today;

                return (
                  <tr key={asset.asset_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-sm text-gray-600">{asset.asset_id}</td>
                    <td className="px-4 py-4"><div className="font-medium">{asset.asset_name}</div><div className="text-xs md:text-sm text-gray-600">{asset.model_number} | {asset.serial_number}</div></td>
                    <td className="px-4 py-4">{asset.category}</td>
                    <td className="px-4 py-4">{asset.vendor_name}</td>
                    <td className="px-4 py-4">{asset.assigned_to}</td>
                    <td className="px-4 py-4">{asset.location}</td>
                    <td className="px-4 py-4"><span className={getStatusBadgeClass(asset.condition)}>{asset.condition}</span></td>
                    <td className="px-4 py-4"><span className={getStatusBadgeClass(asset.status)}>{asset.status}</span></td>
                    <td className="px-4 py-4">
                      <div className={`${isExpired ? "text-red-600" : isExpiringSoon ? "text-orange-600" : ""}`}>
                        {asset.warranty_expiry_date ? new Date(asset.warranty_expiry_date).toLocaleDateString() : "N/A"}
                        {isExpiringSoon && <div className="flex items-center text-xs text-orange-600 mt-1"><AlertTriangle className="h-3 w-3 mr-1" />Expires in {daysUntilExpiry} days</div>}
                        {isExpired && <div className="flex items-center text-xs text-red-600 mt-1"><AlertTriangle className="h-3 w-3 mr-1" />Expired</div>}
                      </div>
                    </td>
                    <td className="px-4 py-4"><div>Rs{asset.current_value.toLocaleString()}</div><div className="text-xs text-gray-600">Original: Rs{asset.purchase_cost.toLocaleString()}</div></td>
                    <td className="px-4 py-4 text-sm">
                      <div className="font-medium">Total: {qTotal}</div>
                      <div className="text-xs text-gray-600 mt-0.5">In use: {inSvc}<span className="mx-1">·</span>Repair: {qRepair}</div>
                      {spare > 0 && <div className="text-xs text-muted-foreground mt-0.5">Available / storage: {spare}</div>}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        <Button variant="ghost" size="sm" className="text-[#0263cc] hover:text-[#0263cc]/80 p-1" aria-label="Edit asset" onClick={() => openEditForm(asset)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-800 p-1" aria-label="Add maintenance record" onClick={() => { setSelectedAsset(asset); setShowMaintenanceForm(true); }}><Wrench className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-800 p-1" aria-label="View details" onClick={() => openViewDialog(asset)}><Eye className="h-4 w-4" /></Button>
                      </div>
                      {maintenanceDue && <div className="flex items-center text-xs text-orange-600 mt-1"><AlertTriangle className="h-3 w-3 mr-1" />Maintenance Due</div>}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalAssets}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        currentPageCount={assets.length}
        itemLabel="assets"
      />
    </>
  );
}
